export type AudienceRuleType = "Add" | "Evaluate" | "Remove";

export interface AudienceRuleParameter {
  Name: string;
  DataType: string;
  EntityName?: string;
  Value?: unknown;
  Caption?: string;
}

/** A rule as the audience stores it, and as the catalogue offers it. */
export interface AudienceRule {
  Caption: string;
  Category: string;
  Class: string;
  Entity: string;
  Id: string;
  Name: string;
  Parameters: AudienceRuleParameter[];
  RunAt: string;
  Type: AudienceRuleType;
  IsMissing?: boolean;
}

export const AudienceRuleTypes: AudienceRuleType[] = [
  "Add",
  "Evaluate",
  "Remove",
];

const TypeLabels: Record<AudienceRuleType, string> = {
  Add: "Add",
  Evaluate: "Keep",
  Remove: "Remove",
};

export const ruleTypeLabel = (type: AudienceRuleType) => TypeLabels[type];

const PlaceholderPattern = /\{\d:\[(.*?)\]\}/g;
const LabelPattern = /(.*?)\{\d:\[(.*?)\]\}/g;

export function newAudienceRule(
  type: AudienceRuleType,
  runAt: string
): AudienceRule {
  return {
    Caption: "",
    Category: "",
    Class: "",
    Entity: "",
    Id: "",
    Name: "",
    Parameters: [],
    RunAt: runAt,
    Type: type,
  };
}

/** The first rule has to build the audience, so only an Add can open it. */
export function canPlaceRule(
  rules: AudienceRule[],
  type: AudienceRuleType,
  index: number
) {
  return type === "Add" || (rules.length > 0 && index > 0);
}

export function insertRule(
  rules: AudienceRule[],
  index: number,
  rule: AudienceRule
) {
  const next = [...rules];
  next.splice(Math.max(0, Math.min(index, rules.length)), 0, rule);

  return next;
}

export function removeRule(rules: AudienceRule[], index: number) {
  return rules.filter((_, position) => position !== index);
}

/** Moves a rule to `to`, or returns the rules as they were if that would put a non-Add first. */
export function moveRule(rules: AudienceRule[], from: number, to: number) {
  if (from === to || !rules[from]) {
    return rules;
  }

  const next = [...rules];
  const [rule] = next.splice(from, 1);
  next.splice(to, 0, rule);

  return next[0].Type === "Add" ? next : rules;
}

/** The text before each placeholder of a caption template, which names that parameter. */
export function parameterLabels(caption: string) {
  return Array.from(caption.matchAll(LabelPattern)).map((match) =>
    match[1].trim()
  );
}

/** The rule as one line: "Add Contacts who have bought a ticket to Arsenal". */
export function ruleSummary(rule: AudienceRule) {
  const { Caption, Parameters } = rule;
  let summary = Caption ?? "";

  if (Caption && Parameters.length > 0) {
    let index = 0;
    summary = Caption.replace(PlaceholderPattern, () => {
      const parameter = Parameters[index++];

      return String(
        (parameter?.DataType.toLowerCase() === "entity"
          ? parameter.Caption
          : parameter?.Value) ?? ""
      );
    });
  }

  const text = `${ruleTypeLabel(rule.Type).toUpperCase()} ${summary}`.trim();

  return rule.IsMissing ? `${text} : ---MISSING RULE---` : text;
}

/** A caption template for choosing between rules: its placeholders as [what goes there]. */
export const readableCaption = (caption: string) =>
  caption.replace(PlaceholderPattern, "[$1]");

/** Takes the catalogue's rule for this one: what it is, with its parameters still to fill. */
export function chooseCatalogueRule(
  rule: AudienceRule,
  chosen: AudienceRule
): AudienceRule {
  return {
    ...rule,
    Id: chosen.Id,
    Caption: chosen.Caption,
    Category: chosen.Category,
    Class: chosen.Class,
    Entity: chosen.Entity,
    Name: chosen.Name,
    Parameters: chosen.Parameters.map((parameter) => ({
      ...parameter,
      Value: "",
      Caption: "",
    })),
  };
}

/**
 * Sets one parameter. `lookupLabel` is the chosen record's name for an entity parameter;
 * any other keeps the caption's own text beside it, as the audience has always stored.
 */
export function setRuleParameter(
  rule: AudienceRule,
  name: string,
  value: unknown,
  lookupLabel?: string
): AudienceRule {
  const labels = parameterLabels(rule.Caption);

  return {
    ...rule,
    Parameters: rule.Parameters.map((parameter, index) =>
      parameter.Name === name
        ? {
            ...parameter,
            Value: value,
            Caption:
              parameter.DataType.toLowerCase() === "entity"
                ? lookupLabel ?? ""
                : labels[index] ?? "",
          }
        : parameter
    ),
  };
}

const isBlank = (value: unknown) =>
  value === undefined || value === null || String(value).trim() === "";

export function isRuleComplete(rule: AudienceRule) {
  return (
    Boolean(rule.Id) &&
    rule.Parameters.every((parameter) => !isBlank(parameter.Value))
  );
}

/** What is wrong with each rule that cannot be saved yet, by its position. */
export function validateRules(rules: AudienceRule[]) {
  const errors: Record<number, string> = {};

  rules.forEach((rule, index) => {
    if (!rule.Id) {
      errors[index] = "Choose a rule";
    } else if (!isRuleComplete(rule)) {
      errors[index] = "Fill in the rule's values";
    }
  });

  return errors;
}

/** Ids are guids, which the service may write in either case. */
export const sameId = (left: unknown, right: unknown) =>
  String(left ?? "").toLowerCase() === String(right ?? "").toLowerCase();

/**
 * What the properties pane shows for a rule: its category and parameters taken from the
 * catalogue entry it is an instance of, with the values the rule holds. A saved rule is
 * not relied on to carry them, so the pane fills in whatever the catalogue knows.
 */
export function describeRule(rule: AudienceRule, catalogue: AudienceRule[]) {
  const entry = catalogue.find((candidate) => sameId(candidate.Id, rule.Id));
  const held = (name: string) =>
    rule.Parameters.find((parameter) => parameter.Name === name);
  const caption = entry?.Caption || rule.Caption;
  const parameters = (entry?.Parameters ?? rule.Parameters).map((definition) => ({
    ...definition,
    Value: held(definition.Name)?.Value ?? definition.Value ?? "",
    Caption: held(definition.Name)?.Caption ?? definition.Caption ?? "",
  }));

  return {
    known: Boolean(entry),
    entity: entry?.Entity ?? "",
    entryId: entry?.Id ?? rule.Id,
    category: entry?.Category || rule.Category || "",
    caption,
    parameters,
    labels: parameterLabels(caption ?? ""),
  };
}

// A blank entity means there is none to narrow by.
const ofEntity = (rule: AudienceRule, entity: string) =>
  !entity || rule.Entity === entity;

export const categoriesFor = (
  catalogue: AudienceRule[],
  entity: string
): string[] =>
  Array.from(
    new Set(
      catalogue
        .filter((rule) => ofEntity(rule, entity) && rule.Category)
        .map((r) => r.Category)
    )
  ).sort((left, right) => left.localeCompare(right));

export const rulesInCategory = (
  catalogue: AudienceRule[],
  entity: string,
  category: string
) =>
  catalogue.filter(
    (rule) => ofEntity(rule, entity) && rule.Category === category
  );
